import React, { useState, useEffect } from 'react';
import styles from './ChargeSession.module.css';
import useNotificationChannels from '../useNotificationChannels';

function fmt(n) { return `$${Number(n).toFixed(2)}`; }

export default function ChargeSession({ clients, config, refresh, showToast, API, initialClientId, authHeader, onNavigate }) {
  const [step, setStep] = useState(initialClientId ? 2 : 1);
  const [selectedClient, setSelectedClient] = useState(
    initialClientId ? (clients.find(c => c.id === initialClientId) || null) : null
);
  const [sessionType, setSessionType] = useState('');
  const [athleteCount, setAthleteCount] = useState(1);
  const [sessionDate, setSessionDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [selectedAthleteIds, setSelectedAthleteIds] = useState([]);
  // Per-athlete rate override when the selected session type has
  // allowCustomPrice=true. Pre-fills from the configured rate when the type is
  // picked; admin can change before charging. $0 allowed (Free Eval case).
  const [customRate, setCustomRate] = useState('');

  const channels = useNotificationChannels('sessionCharged', API, authHeader);
  const enabledLabel = [channels.email && 'email', channels.sms && 'SMS'].filter(Boolean).join(' + ');
  const anyChannelEnabled = channels.email || channels.sms;

const filteredClients = clients.filter(c => {
  const q = search.toLowerCase();
  const athletes = c.athletes?.map(a => a.name).join(' ') || c.athlete || '';
  return c.name.toLowerCase().includes(q) || athletes.toLowerCase().includes(q);
});

  const rate = config?.sessionRates?.[sessionType];
  const isPerAthlete = sessionType === 'pairs' || sessionType === 'group';
  const clientAthletes = selectedClient?.athletes || [];
  // When the client has athletes on file, the picker is the source of truth for
  // both billing count and which kids' names land on the receipt. Fall back to
  // the manual +/- counter for legacy clients (only `c.athlete` string).
  const useAthletePicker = clientAthletes.length > 0;
  const count = isPerAthlete
    ? (useAthletePicker ? Math.max(1, selectedAthleteIds.length) : parseInt(athleteCount))
    : 1;
  // Effective per-athlete rate: custom override when the type permits one and a
  // valid value is present; otherwise the configured rate. Empty/NaN custom
  // input falls back to the configured rate so we never charge garbage.
  const canCustom = !!rate?.allowCustomPrice;
  const customRateNum = parseFloat(customRate);
  const effectiveRate = canCustom && Number.isFinite(customRateNum) && customRateNum >= 0
    ? customRateNum
    : (rate?.rate ?? 0);
  const chargeAmount = rate ? effectiveRate * count : 0;
  const newBalance = selectedClient ? selectedClient.balance - chargeAmount : 0;
  const insufficient = selectedClient && chargeAmount > selectedClient.balance;

  // When the client or session type changes, re-default the athlete picker:
  // single-athlete sessions → first athlete; multi-athlete sessions → all athletes.
  useEffect(() => {
    if (!useAthletePicker) { setSelectedAthleteIds([]); return; }
    if (!sessionType) return;
    if (isPerAthlete) {
      setSelectedAthleteIds(clientAthletes.map(a => a.id));
    } else {
      setSelectedAthleteIds(clientAthletes.length > 0 ? [clientAthletes[0].id] : []);
    }
  }, [selectedClient?.id, sessionType]);

  // When session type changes: pre-fill the custom rate input with the
  // configured per-athlete rate (if that type permits custom pricing), or clear
  // it. The admin can then edit or leave it as-is.
  useEffect(() => {
    if (rate?.allowCustomPrice) {
      setCustomRate(String(rate.rate ?? ''));
    } else {
      setCustomRate('');
    }
  }, [sessionType, rate?.allowCustomPrice, rate?.rate]);

  const reset = () => {
    setStep(1);
    setSelectedClient(null); setSessionType(''); setCustomRate('');
    setAthleteCount(1); setNotes(''); setReceipt(null); setSearch('');
    setSessionDate(new Date().toISOString().slice(0, 10));
    setSendEmail(true);
  };

  const handleCharge = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/clients/${selectedClient.id}/charge-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({
          sessionType,
          athleteCount: count,
          sessionDate,
          notes,
          suppressEmail: !sendEmail,
          athleteIds: useAthletePicker ? selectedAthleteIds : undefined,
          // Only send customRate when the type permits one; backend ignores
          // otherwise but no point putting noise in the request.
          customRate: canCustom ? effectiveRate : undefined,
        })
      });
      if (!res.ok) {
        const err = await res.json();
        showToast(err.error || 'Charge failed', 'error');
        return;
      }
      const data = await res.json();
      setReceipt(data.receipt);
      setStep(4);
      refresh();
      showToast(sendEmail ? `Session charged. Receipt sent to ${selectedClient.email}` : 'Session charged. No email sent.');
    } catch (e) {
      showToast('Network error — is the backend running?', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      {/* Progress bar */}
      {step < 4 && (
        <div className={styles.progress}>
          {['Select Client', 'Session Type', 'Confirm'].map((label, i) => (
            <div key={i} className={`${styles.progressStep} ${step > i + 1 ? styles.done : ''} ${step === i + 1 ? styles.current : ''}`}>
              <div className={styles.progressDot}>{step > i + 1 ? '✓' : i + 1}</div>
              <span>{label}</span>
            </div>
          ))}
        </div>
      )}

      {/* STEP 1: Select Client */}
      {step === 1 && (
        <div className={styles.stepCard}>
          <h2 className={styles.stepTitle}>Select Client</h2>
          <input
            className={styles.searchInput}
            placeholder="Search by parent or athlete name..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            autoFocus
          />
          <div className={styles.clientGrid}>
            {filteredClients.map(c => (
              <button
                key={c.id}
                className={`${styles.clientCard} ${c.balance <= 0 ? styles.clientEmpty : c.balance < 75 ? styles.clientLow : ''}`}
                onClick={() => { setSelectedClient(c); setStep(2); }}
              >
                <div className={styles.clientAvatar}>{c.name.charAt(0)}</div>
                <div className={styles.clientInfo}>
                  <div className={styles.clientName}>{c.name}</div>
                  <div className={styles.clientAthlete}>Athlete: {c.athlete || '—'}</div>
                </div>
                <div className={`${styles.clientBalance} ${c.balance < 75 ? styles.balLow : ''} ${c.balance <= 0 ? styles.balEmpty : ''}`}>
                  {fmt(c.balance)}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* STEP 2: Select Session Type */}
      {step === 2 && selectedClient && config && (
        <div className={styles.stepCard}>
          <div className={styles.stepBackRow}>
            <button className={styles.backBtn} onClick={() => setStep(1)}>← Back</button>
            <div className={styles.selectedClientPill}>
              <span className={styles.scAvatar}>{selectedClient.name.charAt(0)}</span>
              <span>{selectedClient.name}</span>
              <span className={styles.scBal}>{fmt(selectedClient.balance)}</span>
            </div>
          </div>
          <h2 className={styles.stepTitle}>Select Session Type</h2>

          <div className={styles.sessionGrid}>
            {Object.entries(config.sessionRates).map(([key, r]) => (
              <button
                key={key}
                className={`${styles.sessionCard} ${sessionType === key ? styles.sessionSelected : ''}`}
                onClick={() => setSessionType(key)}
              >
                <span className={styles.sessionIcon}>{r.icon}</span>
                <div className={styles.sessionInfo}>
                  <div className={styles.sessionLabel}>{r.label}</div>
                  <div className={styles.sessionDesc}>{r.description}</div>
                </div>
                <div className={styles.sessionRate}>{fmt(r.rate)}</div>
              </button>
            ))}
          </div>

          {sessionType && canCustom && (
            <div className={styles.countRow} style={{ borderTop: '1px dashed var(--border)', paddingTop: 12 }}>
              <label className={styles.countLabel}>
                Custom rate per athlete
                <span className={styles.athletePickerHint || ''} style={{ color: 'var(--gray-3)', fontWeight: 400, fontSize: '0.78rem' }}>
                  {' · pre-filled from Rates page; change as needed'}
                </span>
              </label>
              <div className={styles.countControls} style={{ alignItems: 'center', gap: 6 }}>
                <span style={{ color: 'var(--gray-2)', fontSize: '1.1rem' }}>$</span>
                <input
                  type="number" min="0" step="5"
                  className={styles.fieldInput}
                  style={{ width: 100, textAlign: 'right' }}
                  value={customRate}
                  onChange={e => setCustomRate(e.target.value)}
                  placeholder="0"
                />
              </div>
              <span className={styles.countTotal}>Total: {fmt(chargeAmount)}</span>
            </div>
          )}

          {sessionType && useAthletePicker && (
            <div className={styles.athletePickerRow}>
              <label className={styles.countLabel}>
                {isPerAthlete ? 'Athletes Training' : 'Athlete'}
                <span className={styles.athletePickerHint}>
                  {isPerAthlete ? ' · tap to toggle' : ' · pick one'}
                </span>
              </label>
              <div className={styles.athleteChipRow}>
                {clientAthletes.map(a => {
                  const on = selectedAthleteIds.includes(a.id);
                  return (
                    <button key={a.id} type="button"
                      className={`${styles.athleteChip} ${on ? styles.athleteChipOn : ''}`}
                      onClick={() => {
                        if (isPerAthlete) {
                          setSelectedAthleteIds(prev => on ? prev.filter(x => x !== a.id) : [...prev, a.id]);
                        } else {
                          setSelectedAthleteIds([a.id]);
                        }
                      }}>
                      {on ? '✓ ' : ''}{a.name}
                    </button>
                  );
                })}
              </div>
              {isPerAthlete && <span className={styles.countTotal}>Total: {fmt(chargeAmount)}</span>}
            </div>
          )}

          {sessionType && isPerAthlete && !useAthletePicker && (
            <div className={styles.countRow}>
              <label className={styles.countLabel}>Number of Athletes</label>
              <div className={styles.countControls}>
                <button className={styles.countBtn} onClick={() => setAthleteCount(Math.max(2, count - 1))}>−</button>
                <span className={styles.countVal}>{count}</span>
                <button className={styles.countBtn} onClick={() => setAthleteCount(Math.min(6, count + 1))}>+</button>
              </div>
              <span className={styles.countTotal}>Total: {fmt(chargeAmount)}</span>
            </div>
          )}

          <div className={styles.extraFields}>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Session Date</label>
              <input type="date" className={styles.fieldInput} value={sessionDate}
                onChange={e => setSessionDate(e.target.value)} />
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Notes (optional)</label>
              <input className={styles.fieldInput} placeholder="e.g. QB + WR drill work"
                value={notes} onChange={e => setNotes(e.target.value)} />
            </div>
          </div>

          <button
            className={styles.nextBtn}
            disabled={!sessionType}
            onClick={() => setStep(3)}
          >Review Charge →</button>
        </div>
      )}

      {/* STEP 3: Confirm */}
      {step === 3 && selectedClient && rate && (
        <div className={styles.stepCard}>
          <div className={styles.stepBackRow}>
            <button className={styles.backBtn} onClick={() => setStep(2)}>← Back</button>
          </div>
          <h2 className={styles.stepTitle}>Confirm & Charge</h2>

          {(() => {
            const participants = useAthletePicker
              ? clientAthletes.filter(a => selectedAthleteIds.includes(a.id))
              : [];
            const isMulti = participants.length > 1;
            const perAthlete = effectiveRate;
            return (
          <div className={styles.confirmBox}>
            <div className={styles.confirmRow}>
              <span className={styles.confirmKey}>Client</span>
              <span className={styles.confirmVal}>{selectedClient.name}</span>
            </div>
            {!isMulti && (
              <div className={styles.confirmRow}>
                <span className={styles.confirmKey}>Athlete</span>
                <span className={styles.confirmVal}>
                  {useAthletePicker
                    ? (participants[0]?.name || '—')
                    : (selectedClient.athlete || '—')}
                </span>
              </div>
            )}
            <div className={styles.confirmRow}>
              <span className={styles.confirmKey}>Session</span>
              <span className={styles.confirmVal}>{rate.icon} {rate.label}</span>
            </div>
            <div className={styles.confirmRow}>
              <span className={styles.confirmKey}>Date</span>
              <span className={styles.confirmVal}>{new Date(sessionDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
            </div>
            {notes && <div className={styles.confirmRow}>
              <span className={styles.confirmKey}>Notes</span>
              <span className={styles.confirmVal}>{notes}</span>
            </div>}
            <div className={styles.confirmDivider} />
            <div className={styles.confirmRow}>
              <span className={styles.confirmKey}>Current Balance</span>
              <span className={styles.confirmVal}>{fmt(selectedClient.balance)}</span>
            </div>
            {isMulti ? (
              <>
                {participants.map(a => (
                  <div key={a.id} className={styles.confirmRow}>
                    <span className={styles.confirmKey}>{a.name}</span>
                    <span className={`${styles.confirmVal} ${styles.chargeAmount}`}>− {fmt(perAthlete)}</span>
                  </div>
                ))}
                <div className={styles.confirmRow}>
                  <span className={styles.confirmKey}>Total Charge</span>
                  <span className={`${styles.confirmVal} ${styles.chargeAmount}`}>− {fmt(chargeAmount)}</span>
                </div>
              </>
            ) : (
              <div className={styles.confirmRow}>
                <span className={styles.confirmKey}>Charge</span>
                <span className={`${styles.confirmVal} ${styles.chargeAmount}`}>− {fmt(chargeAmount)}</span>
              </div>
            )}
            <div className={`${styles.confirmRow} ${styles.confirmFinal}`}>
              <span className={styles.confirmKey}>Remaining Balance</span>
              <span className={`${styles.confirmVal} ${insufficient ? styles.balEmpty : newBalance < 75 ? styles.balLow : styles.balOk}`}>
                {fmt(newBalance)}
              </span>
            </div>
          </div>
            );
          })()}

          {insufficient && (
            <div className={styles.errorBox}>
              ⚠ This will overdraw the account to <strong>{fmt(newBalance)}</strong>.
            </div>
          )}

          <label className={`${styles.emailToggle} ${!anyChannelEnabled ? styles.emailToggleDisabled : ''}`}>
            <input
              type="checkbox"
              checked={sendEmail && anyChannelEnabled}
              disabled={!anyChannelEnabled}
              onChange={e => setSendEmail(e.target.checked)}
            />
            <span>
              {anyChannelEnabled
                ? <>Notify {selectedClient.name?.split(' ')[0] || 'parent'} <span style={{ color: 'var(--gray-3)' }}>({enabledLabel})</span></>
                : <>No channels enabled — turn on in Notifications</>}
            </span>
          </label>

          <button
            className={styles.chargeBtn}
            disabled={loading}
            onClick={handleCharge}
          >
            {loading ? 'Processing...' : (sendEmail && anyChannelEnabled) ? `Charge ${fmt(chargeAmount)} & Notify` : `Charge ${fmt(chargeAmount)}`}
          </button>
        </div>
      )}

      {/* STEP 4: Receipt */}
      {step === 4 && receipt && (
        <div className={styles.stepCard}>
          <div className={styles.receiptSuccess}>
            <div className={styles.successIcon}>✓</div>
            <h2 className={styles.successTitle}>Session Charged</h2>
            <p className={styles.successSub}>{receipt.emailSent ? `Receipt sent to ${receipt.clientName}` : `Charged — no email sent`}</p>
          </div>

          <div className={styles.receiptCard}>
            <div className={styles.receiptHeader}>
              <div className={styles.receiptLogo}>
                <span className={styles.receiptLogoText}>NEXT GEN SPORTS</span>
              </div>
              <div className={styles.receiptInvoiceId}>{receipt.invoiceId}</div>
            </div>

            <div className={styles.receiptBody}>
              {(() => {
                const participants = useAthletePicker
                  ? clientAthletes.filter(a => selectedAthleteIds.includes(a.id))
                  : [];
                const isMulti = participants.length > 1;
                const perAthlete = receipt.charged / Math.max(1, count);
                return (
                  <>
                    <div className={styles.receiptRow}>
                      <span>Client</span><span>{receipt.clientName}</span>
                    </div>
                    <div className={styles.receiptRow}>
                      <span>Date</span><span>{receipt.sessionDate}</span>
                    </div>
                    {isMulti ? (
                      participants.map(a => (
                        <div key={a.id} className={styles.receiptRow}>
                          <span>{a.name}</span>
                          <span className={styles.receiptCharge}>− {fmt(perAthlete)}</span>
                        </div>
                      ))
                    ) : (
                      <div className={styles.receiptRow}>
                        <span>Athlete</span><span>{receipt.athleteName}</span>
                      </div>
                    )}
                    <div className={styles.receiptRow}>
                      <span>Session</span><span>{receipt.sessionType}</span>
                    </div>
                    <div className={styles.receiptDivider} />
                    {isMulti ? (
                      <div className={styles.receiptRow}>
                        <span><strong>Total Amount Charged</strong></span>
                        <span className={styles.receiptCharge}>− {fmt(receipt.charged)}</span>
                      </div>
                    ) : (
                      <div className={styles.receiptRow}>
                        <span>Amount Charged</span>
                        <span className={styles.receiptCharge}>− {fmt(receipt.charged)}</span>
                      </div>
                    )}
                    <div className={`${styles.receiptRow} ${styles.receiptBalance}`}>
                      <span>Account Balance Remaining</span>
                      <span className={styles.receiptBalanceVal}>{fmt(receipt.remainingBalance)}</span>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>

          {receipt.remainingBalance < 75 && (
            <div className={styles.lowBalanceWarning}>
              ⚠ Low balance — consider reminding {receipt.clientName} to top up their account.
            </div>
          )}

          <div className={styles.receiptActions}>
            <button className={styles.nextBtn} onClick={reset}>Charge Another Session</button>
            {initialClientId && onNavigate && (
              <button className={styles.returnClientBtn}
                onClick={() => onNavigate('clients', selectedClient?.id)}>
                ← Return to Client
              </button>
            )}
            {!initialClientId && onNavigate && selectedClient && (
              <button className={styles.returnClientBtn}
                onClick={() => onNavigate('clients', selectedClient.id)}>
                ← View Client Record
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
